import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all duration-base ease-standard focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground rounded-pill shadow-soft hover:bg-sunset-500 hover:shadow-lift focus-visible:ring-4 focus-visible:ring-sun-300/55",
        destructive: "bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90",
        outline: "border border-border bg-card text-foreground rounded-pill hover:bg-sand-100 hover:shadow-soft",
        secondary: "bg-secondary text-secondary-foreground border border-border rounded-pill hover:bg-sand-50 hover:shadow-soft focus-visible:ring-4 focus-visible:ring-sun-300/55",
        ghost: "text-forest-700 rounded-lg hover:bg-sand-100 hover:text-forest-900",
        link: "text-forest-700 underline-offset-4 hover:underline hover:text-sunset-600",
      },
      size: {
        default: "h-11 md:h-12 px-5 md:px-6 text-body",
        sm: "h-9 px-4 text-small",
        lg: "h-12 md:h-14 px-6 md:px-8 text-body",
        icon: "h-11 w-11 md:h-12 md:w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };